import type { ComponentProps, FC } from 'react';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Image as RNImage,
  StyleSheet,
  View,
  type ImageErrorEventData,
  type ImageStyle,
} from 'react-native';

import { Text } from '@/components/ui/text';
import { STRINGS } from '@/constants/strings';
import { useScaling } from '@/hooks';
import { ScaleSize } from '@/theme/scaling';

export type ImageSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export type ImageDimension = number | string;

type RN = ComponentProps<typeof RNImage>;

/** Web implementation mirrors native {@link ImageProps} without importing FastImage. */
export type ImageProps = Omit<
  RN,
  | 'style'
  | 'source'
  | 'defaultSource'
  | 'onLoadStart'
  | 'onLoad'
  | 'onError'
  | 'onLoadEnd'
  | 'onProgress'
> & {
  size?: ImageSize;
  rounded?: boolean;
  circular?: boolean;
  width?: ImageDimension;
  height?: ImageDimension;
  style?: RN['style'];
  defaultSource?: RN['source'] | string;
  persistLoadedUri?: boolean;
  source?: RN['source'] | string;
  cachePolicy?: unknown;
  onStart?: () => void;
  onSuccess?: () => void;
  onProgress?: () => void;
  onFailure?: (error: string) => void;
  onCompletion?: () => void;
};

const loadedUriCache = new Set<string>();

const getSizeValue = (size: ImageSize, scale: (size: ScaleSize) => number): number => {
  switch (size) {
    case 'xs':
      return scale(24);
    case 'sm':
      return scale(32);
    case 'md':
      return scale(48);
    case 'lg':
      return scale(64);
    case 'xl':
      return scale(96);
    default:
      return scale(48);
  }
};

const ImageComponent: FC<ImageProps> = memo(
  ({
    size,
    rounded = false,
    circular = false,
    cachePolicy: _cachePolicy,
    width,
    height,
    style,
    defaultSource,
    persistLoadedUri = false,
    onStart,
    onSuccess,
    onFailure,
    onCompletion,
    onProgress: _onProgress,
    source,
    ...props
  }) => {
    const [error, setError] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const { scale } = useScaling();

    const didErrorRef = useRef(false);
    /** Same `source` can fire `onLoadStart` again when layout/style changes (e.g. resize); avoid spinner flicker. */
    const loadSettledForSourceKeyRef = useRef<string | null>(null);

    const sourceUri =
      typeof source === 'string'
        ? source
        : typeof source === 'object' &&
            source != null &&
            'uri' in source &&
            typeof source.uri === 'string'
          ? source.uri
          : undefined;

    const sourceKey =
      typeof source === 'number'
        ? `local:${source}`
        : typeof source === 'string'
          ? `uri:${source}`
          : sourceUri
            ? `uri:${sourceUri}`
            : source
              ? 'object'
              : 'none';

    useEffect(() => {
      setError(false);
      setLoading(false);
      didErrorRef.current = false;
      loadSettledForSourceKeyRef.current = null;
    }, [sourceKey]);

    const imageStyle = useMemo(() => {
      const computedStyle: ImageStyle = {};

      if (size) {
        const sizeValue = getSizeValue(size, scale);
        computedStyle.width = sizeValue;
        computedStyle.height = sizeValue;
      }

      if (width !== undefined) {
        computedStyle.width = width as ImageStyle['width'];
      }
      if (height !== undefined) {
        computedStyle.height = height as ImageStyle['height'];
      }

      if (circular) {
        const w = computedStyle.width;
        const h = computedStyle.height;
        if (typeof w === 'number' && typeof h === 'number') {
          const maxSide = Math.max(w, h);
          if (maxSide > 0) {
            computedStyle.borderRadius = maxSide / 2;
          }
        } else {
          computedStyle.borderRadius = 9999;
        }
      } else if (rounded) {
        computedStyle.borderRadius = scale(8);
      }

      return [computedStyle, style];
    }, [size, rounded, circular, width, height, style, scale]);

    const handleStart = () => {
      setError(false);
      didErrorRef.current = false;
      if (loadSettledForSourceKeyRef.current === sourceKey) {
        onStart?.();
        return;
      }
      if (sourceUri && (!persistLoadedUri || !loadedUriCache.has(sourceUri))) {
        setLoading(true);
      }
      onStart?.();
    };

    const handleLoad = () => {
      didErrorRef.current = false;
      setError(false);
      setLoading(false);
      onSuccess?.();
    };

    const handleError = (e: { nativeEvent: ImageErrorEventData }) => {
      didErrorRef.current = true;
      setError(true);
      setLoading(false);
      const msg =
        typeof e.nativeEvent?.error === 'string' ? e.nativeEvent.error : 'Image load error';
      onFailure?.(msg);
    };

    const handleCompletion = () => {
      setLoading(false);
      if (!didErrorRef.current) {
        loadSettledForSourceKeyRef.current = sourceKey;
      }
      if (persistLoadedUri && sourceUri) {
        loadedUriCache.add(sourceUri);
      }
      onCompletion?.();
    };

    const resolveSource = (
      value: ImageProps['source'],
    ): ComponentProps<typeof RNImage>['source'] => {
      if (value == null) return undefined;
      if (typeof value === 'string') return { uri: value };
      if (typeof value === 'number') return value;
      if (
        typeof value === 'object' &&
        value !== null &&
        'uri' in value &&
        typeof value.uri === 'string'
      ) {
        return value as ComponentProps<typeof RNImage>['source'];
      }
      return value as ComponentProps<typeof RNImage>['source'];
    };

    const resolveDefaultSource = (
      value: ImageProps['defaultSource'],
    ): ComponentProps<typeof RNImage>['source'] => {
      if (value == null) return undefined;
      if (typeof value === 'string') return { uri: value };
      if (typeof value === 'number') return value;
      if (typeof value === 'object' && value !== null) {
        return value as ComponentProps<typeof RNImage>['source'];
      }
      return value as ComponentProps<typeof RNImage>['source'];
    };

    const renderError = () => {
      if (!defaultSource) {
        return (
          <View style={styles.errorContainer}>
            <Text>{STRINGS.common.imageRenderError}</Text>
          </View>
        );
      }

      return (
        <RNImage
          source={resolveDefaultSource(defaultSource)}
          style={imageStyle}
          resizeMode={props.resizeMode}
        />
      );
    };

    const renderImage = () => {
      if (!source) return null;

      return (
        <RNImage
          key={sourceKey}
          source={resolveSource(source)}
          style={imageStyle}
          resizeMode={props.resizeMode}
          onLoadStart={handleStart}
          onLoad={handleLoad}
          onError={handleError}
          onLoadEnd={handleCompletion}
          {...props}
        />
      );
    };

    return (
      <View style={imageStyle}>
        {!error && renderImage()}

        {loading && (
          <View style={styles.loadingContainer}>
            <ActivityIndicator />
          </View>
        )}

        {error && renderError()}
      </View>
    );
  },
);

export const Image = memo(ImageComponent);

Image.displayName = 'Image';

const styles = StyleSheet.create({
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
  },
});
